import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { VariableChip } from "./chip";

export type VariableCategory = "person" | "sender" | "job";
export type VariableFallbackPolicy = "block_enrollment" | "literal_fallback";

export interface VariableAttrs {
  key: string;
  category: VariableCategory;
  label: string;
  fallback_policy: VariableFallbackPolicy;
  fallback_literal: string | null;
}

export interface VariableMentionOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    variableMention: {
      insertVariableMention: (attrs: VariableAttrs) => ReturnType;
    };
  }
}

/**
 * `variable-mention` ProseMirror node.
 *
 * Spec: docs/superpowers/specs/2026-05-07-outreach-01-ai-prompts-design.md §5.1.
 *
 * We extend `Node.create` directly (rather than `Mention.extend`) because we
 * carry multiple non-trigger attrs (`category`, `label`, `fallback_policy`,
 * `fallback_literal`) and don't need Mention's `id`/`label` defaults.
 *
 * The wire format is `editor.getJSON()` — backend `instructions_doc` (jsonb)
 * stores ProseMirror JSON verbatim. `parseHTML`/`renderHTML` exist for clipboard
 * paste and for the in-editor visual fallback when the React node view is not
 * yet attached. Persistence and the backend renderer never go through HTML.
 *
 * The chip's interactive React node view is registered in
 * `./variable-chip.tsx` and attached to this node via `addNodeView` in a later
 * task; this file owns the schema-level contract only.
 */
export const VariableMention = Node.create<VariableMentionOptions>({
  name: "variable-mention",

  inline: true,
  group: "inline",
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      key: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-key") ?? "",
        renderHTML: (attrs) => ({ "data-key": attrs.key }),
      },
      category: {
        default: "person" satisfies VariableCategory,
        parseHTML: (el) =>
          (el.getAttribute("data-category") as VariableCategory | null) ??
          "person",
        renderHTML: (attrs) => ({ "data-category": attrs.category }),
      },
      label: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-label") ?? "",
        renderHTML: (attrs) => ({ "data-label": attrs.label }),
      },
      fallback_policy: {
        default: "block_enrollment" satisfies VariableFallbackPolicy,
        parseHTML: (el) =>
          (el.getAttribute(
            "data-fallback-policy",
          ) as VariableFallbackPolicy | null) ?? "block_enrollment",
        renderHTML: (attrs) => ({
          "data-fallback-policy": attrs.fallback_policy,
        }),
      },
      fallback_literal: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-fallback-literal"),
        renderHTML: (attrs) =>
          attrs.fallback_literal != null
            ? { "data-fallback-literal": attrs.fallback_literal }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="variable-mention"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const label = node.attrs.label || node.attrs.key;
    return [
      "span",
      mergeAttributes(
        { "data-type": "variable-mention", contenteditable: "false" },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      `{${label}}`,
    ];
  },

  renderText({ node }) {
    return `{${node.attrs.label || node.attrs.key}}`;
  },

  addCommands() {
    return {
      insertVariableMention:
        (attrs: VariableAttrs) =>
        ({ chain }) =>
          chain()
            .focus()
            .insertContent({ type: this.name, attrs })
            .run(),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableChip);
  },
});
